import type { Player } from '../types/player';
import { useClubSettings } from '../contexts/ClubSettingsContext';

/**
 * Alapból ennyi nappal a lejárat előtt figyelmeztetünk a sportorvosira. A klub a
 * Rendszerbeállításokban módosíthatja - a feliratok a hookkal kérik le.
 *
 * A szerveroldali párja: ClubTracker.Core/Services/SportDoctorPolicy.cs.
 */
export const DEFAULT_SPORT_DOCTOR_WARNING_DAYS = 60;

/** A klub beállított figyelmeztetési ablaka napokban. */
export const useSportDoctorWarningDays = (): number =>
  useClubSettings().settings?.sportDoctorWarningDays ?? DEFAULT_SPORT_DOCTOR_WARNING_DAYS;

export type MedicalStatus = 'valid' | 'expired' | 'missing';

/**
 * A sportorvosi érvényesség állapota. A játékoslista és a játékos adatlap
 * ugyanezt használja, hogy ne csússzon el egymástól a két nézet.
 */
export const getMedicalStatus = (player: Player): MedicalStatus => {
  if (!player.sportDoctorValidity) {
    return 'missing';
  }

  const expiry = new Date(player.sportDoctorValidity);
  if (isNaN(expiry.getTime())) {
    return 'missing';
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const expiryDate = new Date(expiry);
  expiryDate.setHours(0, 0, 0, 0);

  return expiryDate < today ? 'expired' : 'valid';
};

export const formatMedicalDate = (value?: string): string => {
  if (!value) return '';
  const date = new Date(value);
  return isNaN(date.getTime()) ? value : date.toLocaleDateString('hu-HU');
};

/** Részletesebb állapot nagy listákhoz: az érvényesen belül külön a hamarosan lejárók. */
export type MedicalState = 'missing' | 'expired' | 'expiring' | 'valid';

export const getMedicalState = (player: Player, warningDays: number): MedicalState => {
  const status = getMedicalStatus(player);
  if (status !== 'valid') return status;
  const daysLeft = (new Date(player.sportDoctorValidity!).getTime() - Date.now()) / 86_400_000;
  return daysLeft <= warningDays ? 'expiring' : 'valid';
};

/** Az állapot jelvénye: felirat és színek, a lista és a sportorvosi ablak közösen használja. */
export const medicalBadge = (player: Player, state: MedicalState): { text: string; className: string } => {
  switch (state) {
    case 'missing':
      return { text: 'Nincs adat', className: 'bg-red-100 text-red-800' };
    case 'expired':
      return { text: `Lejárt ${formatMedicalDate(player.sportDoctorValidity)}`, className: 'bg-red-100 text-red-800' };
    case 'expiring':
      return { text: `Lejár ${formatMedicalDate(player.sportDoctorValidity)}`, className: 'bg-amber-100 text-amber-800' };
    default:
      return { text: formatMedicalDate(player.sportDoctorValidity), className: 'bg-green-100 text-green-800' };
  }
};
